import { Box, SxProps, Typography } from '@mui/material';
import { Dropdown } from 'common/form';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import {
  useInterviewStagesFetcher,
  useRejectedReasonsFetcher
} from 'services/integration/hooks';
import { isInterviewStageBasedRejection } from 'services/integration/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from 'components/Tooltip';

const container: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  width: '100%'
};

const unsupportedContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 1
};

type Props = {
  atsJobId: string | undefined | null;
  isRejectionMode: boolean;
  atsRejectedReasonId: string | undefined | null;
  atsInterviewStageId: string | undefined | null;
  komboRejectionReasonId?: string | undefined | null;
  komboStageId?: string | undefined | null;
  setAtsRejectedReasonId: (value: string | undefined) => void;
  setAtsInterviewStageId: (value: string | undefined) => void;
  setKomboRejectionReasonId: (value: string | undefined) => void;
  setKomboStageId: (value: string | undefined) => void;
  error?: boolean;
};

export const AtsUpdateSelector: React.FC<Props> = ({
  atsJobId,
  isRejectionMode,
  atsRejectedReasonId,
  atsInterviewStageId,
  komboRejectionReasonId,
  komboStageId,
  setAtsRejectedReasonId,
  setAtsInterviewStageId,
  setKomboRejectionReasonId,
  setKomboStageId,
  error
}) => {
  const integration = useRecoilValue(integrationAtom);
  // LM/Pinpoint on Kombo: no working reject endpoint → model rejection as
  // a stage move. Treat the REJECTED-rule branch as if it were any other
  // status transition, both for the dropdown source and the write fields.
  const useStagesForRejection = isInterviewStageBasedRejection(integration);
  const isRejectionReasonMode = isRejectionMode && !useStagesForRejection;

  const {
    interviewStages,
    isLoading: isLoadingInterviewStages,
    isUnsupportedProvider: isUnsupportedProviderForInterviewStages
  } = useInterviewStagesFetcher(atsJobId);

  const { rejectedReasons, isLoading: isRejectionReasonsLoading } =
    useRejectedReasonsFetcher(atsJobId);

  const { dropDownOptions, value, selectionText } = useMemo(() => {
    if (isRejectionReasonMode) {
      return {
        dropDownOptions: rejectedReasons.map((reason) => ({
          label: reason.label,
          value: reason.id
        })),
        value: komboRejectionReasonId || atsRejectedReasonId,
        selectionText: 'Choose rejection reason',
        placeholder: 'Reason for rejection'
      };
    }

    return {
      dropDownOptions: interviewStages.map((stage) => ({
        label: stage.name,
        value: stage.id
      })),
      value: komboStageId || atsInterviewStageId,
      selectionText: 'Choose interview stage',
      placeholder: 'Interview stage'
    };
  }, [
    atsInterviewStageId,
    atsRejectedReasonId,
    komboStageId,
    komboRejectionReasonId,
    interviewStages,
    isRejectionReasonMode,
    rejectedReasons
  ]);

  const onOptionSelect = (value: string) => {
    if (isRejectionReasonMode) {
      const reason = rejectedReasons.find((r) => r.id === value);
      setAtsRejectedReasonId(reason?.remoteId || value);
      setKomboRejectionReasonId(reason?.remoteId ? value : undefined);
      setAtsInterviewStageId(undefined);
      setKomboStageId(undefined);
      return;
    }

    const stage = interviewStages.find((s) => s.id === value);
    setAtsInterviewStageId(stage?.remoteId || value);
    setKomboStageId(stage?.remoteId ? value : undefined);
    setAtsRejectedReasonId(undefined);
    setKomboRejectionReasonId(undefined);
  };

  if (isUnsupportedProviderForInterviewStages && !isRejectionReasonMode) {
    return (
      <Box sx={unsupportedContainer}>
        <Typography variant="body2">Unsupported</Typography>
        <Tooltip title="Your ATS does not support global candidate stages. To create an automation to change candidate stage, go to 'Automations' in each individual analysis after it has been created here.">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={container}>
      <Dropdown
        field={{
          options: dropDownOptions,
          buttonText: selectionText,
          isLoading: isLoadingInterviewStages || isRejectionReasonsLoading
        }}
        onChange={onOptionSelect}
        error={error}
        value={value}
      />
    </Box>
  );
};
